import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { User, LogOut, Package, Loader2, Truck, BadgePercent, Wallet, ChevronDown, Gift, Ticket, Mail, Zap, Check } from 'lucide-react';
import {
  customerRegister, customerLogin, customerMe, customerOrders, customerAffiliate, customerCreditHistory,
  customerForgotPassword, customerResetPassword, customerSetBirthday, customerRedeemGiftCard,
  getCustomerToken, setCustomerToken, clearCustomerToken, http,
} from '../lib/api';
import { useToast } from '../hooks/use-toast';

const STATUS_STYLE = {
  'Ordered': 'text-alp-cream/80 border-alp-line',
  'Payment Pending': 'text-amber-300 border-amber-300/40',
  'Payment Confirmed': 'text-emerald-300 border-emerald-300/40',
  'Packed': 'text-sky-300 border-sky-300/40',
  'Shipped': 'text-sky-300 border-sky-300/40',
  'Delivered': 'text-emerald-300 border-emerald-300/40',
  'Cancelled': 'text-red-300 border-red-300/40',
};

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || '').filter(Boolean).join(' ');
  return e?.message || 'Something went wrong. Please try again.';
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const bdayLabel = (b) => {
  const [m, d] = (b || '').split('-').map(Number);
  return m ? `${d} ${MONTHS[m - 1]}` : b;
};

export default function Account() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const resetToken = searchParams.get('reset');
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [mode, setMode] = useState(resetToken ? 'reset' : 'login'); // login | register | forgot | reset
  const [form, setForm] = useState({ fullName: '', email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [affiliate, setAffiliate] = useState([]);
  const [creditHistory, setCreditHistory] = useState([]);
  const [showLedger, setShowLedger] = useState(false);
  const [bday, setBday] = useState({ day: '', month: '' });
  const [editBday, setEditBday] = useState(false);
  const [bdaySaving, setBdaySaving] = useState(false);
  const [gcCode, setGcCode] = useState('');
  const [gcBusy, setGcBusy] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [prefsBusy, setPrefsBusy] = useState(false);

  const custCfg = () => ({ headers: { Authorization: `Bearer ${getCustomerToken()}` } });

  const togglePref = async (key) => {
    const next = { newStock: prefs.newStock, specials: prefs.specials, [key]: !prefs[key] };
    setPrefsBusy(true);
    try {
      await http.put('/customer/email-prefs', next, custCfg());
      setPrefs((p) => ({ ...p, ...next }));
      toast({ title: next[key] ? 'Subscribed ✓' : 'Unsubscribed' });
    } catch (e) { toast({ title: 'Could not update preference', variant: 'destructive' }); }
    finally { setPrefsBusy(false); }
  };

  const redeemGiftCard = async () => {
    if (!gcCode.trim()) { toast({ title: 'Enter your gift card code', variant: 'destructive' }); return; }
    setGcBusy(true);
    try {
      const res = await customerRedeemGiftCard(gcCode.trim());
      setUser((u) => ({ ...u, credit: res.credit }));
      setGcCode('');
      customerCreditHistory().then(setCreditHistory).catch(() => {});
      toast({ title: `Gift card redeemed 🎁 +$${res.amount.toFixed(2)}`, description: `Your store credit is now $${res.credit.toFixed(2)} — use it at checkout.` });
    } catch (err) { toast({ title: 'Could not redeem', description: errText(err), variant: 'destructive' }); }
    finally { setGcBusy(false); }
  };

  const saveBirthday = async () => {
    if (!bday.day || !bday.month) { toast({ title: 'Pick your day and month', variant: 'destructive' }); return; }
    setBdaySaving(true);
    try {
      const res = await customerSetBirthday(Number(bday.day), Number(bday.month));
      setUser((u) => ({ ...u, birthday: res.birthday }));
      setEditBday(false);
      toast({ title: 'Birthday saved 🎁', description: "We'll send you a little treat on your day." });
    } catch (err) { toast({ title: 'Could not save', description: errText(err), variant: 'destructive' }); }
    finally { setBdaySaving(false); }
  };

  useEffect(() => {
    if (resetToken) { setChecking(false); return; } // show reset form regardless of session
    if (!getCustomerToken()) { setChecking(false); return; }
    customerMe()
      .then((u) => setUser(u))
      .catch(() => clearCustomerToken())
      .finally(() => setChecking(false));
  }, [resetToken]);

  useEffect(() => {
    if (!user) return;
    setOrdersLoading(true);
    customerOrders().then(setOrders).catch(() => {}).finally(() => setOrdersLoading(false));
    customerAffiliate().then(setAffiliate).catch(() => setAffiliate([]));
    http.get('/customer/email-prefs', custCfg()).then((r) => setPrefs(r.data)).catch(() => {});
    customerCreditHistory().then(setCreditHistory).catch(() => setCreditHistory([]));
  }, [user]);

  const setf = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === 'forgot') {
        const res = await customerForgotPassword(form.email.trim());
        toast({ title: 'Check your email', description: res.message });
        setMode('login');
        return;
      }
      if (mode === 'reset') {
        const res = await customerResetPassword(resetToken, form.password);
        setCustomerToken(res.token);
        setUser(res.customer);
        setSearchParams({});
        toast({ title: 'Password updated', description: 'You are now signed in.' });
        return;
      }
      const res = mode === 'register'
        ? await customerRegister(form.fullName.trim(), form.email.trim(), form.password)
        : await customerLogin(form.email.trim(), form.password);
      setCustomerToken(res.token);
      setUser(res.customer);
      toast({ title: mode === 'register' ? 'Account created' : 'Welcome back', description: 'You can now view your orders.' });
    } catch (err) {
      toast({ title: 'Unable to continue', description: errText(err), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const logout = () => {
    clearCustomerToken();
    setUser(null);
    setOrders([]);
    setForm({ fullName: '', email: '', password: '' });
    setMode('login');
  };

  if (checking) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-alp-gold" size={28} />
      </div>
    );
  }

  // ---------- Logged in: order history ----------
  if (user) {
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-14">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-alp-gold mb-2">My Account</p>
            <h1 className="font-serif text-3xl md:text-4xl text-alp-cream heading-glow">{user.fullName || 'Welcome'}</h1>
            <p className="text-alp-gray text-sm mt-1" data-testid="account-email">{user.email}</p>
            {Number(user.credit || 0) > 0 && (
              <p className="inline-flex items-center gap-2 mt-3 border border-emerald-400/40 bg-emerald-400/5 rounded-full px-4 py-1.5 text-emerald-300 text-sm" data-testid="account-credit">
                Store credit: ${Number(user.credit).toFixed(2)} <span className="text-alp-gray text-xs">&middot; use it at checkout</span>
              </p>
            )}
          </div>
          <button onClick={logout} data-testid="account-logout-btn" className="inline-flex items-center gap-2 border border-alp-line text-alp-cream/80 px-4 py-2 text-[11px] tracking-[0.15em] uppercase hover:text-alp-gold hover:border-alp-gold transition-colors">
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <div className="mb-10" data-testid="account-birthday">
          {user.birthday && !editBday ? (
            <div className="border border-alp-gold/30 rounded-lg bg-alp-card/40 p-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-alp-cream/90 text-sm flex items-center gap-2">
                <Gift size={16} className="text-alp-gold shrink-0" />
                Birthday on file: <span className="text-alp-gold" data-testid="birthday-value">{bdayLabel(user.birthday)}</span>
                <span className="text-alp-gray">— expect a treat from us on your day 🎂</span>
              </p>
              <button onClick={() => { const [m, d] = user.birthday.split('-'); setBday({ day: String(Number(d)), month: String(Number(m)) }); setEditBday(true); }}
                data-testid="birthday-edit-btn" className="text-alp-gold text-xs underline underline-offset-4 hover:text-alp-goldlight">Change</button>
            </div>
          ) : (
            <div className="border border-alp-gold/30 rounded-lg bg-alp-card/40 p-5">
              <p className="text-alp-cream/90 text-sm mb-3 flex items-center gap-2"><Gift size={16} className="text-alp-gold shrink-0" /> Add your birthday — we'll send you a special treat on your day 🎁</p>
              <div className="flex flex-wrap gap-2 items-center">
                <select value={bday.day} onChange={(e) => setBday((b) => ({ ...b, day: e.target.value }))} data-testid="birthday-day-select"
                  className="bg-alp-dark border border-alp-line rounded px-3 py-2 text-sm text-alp-cream focus:border-alp-gold outline-none">
                  <option value="">Day</option>
                  {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                </select>
                <select value={bday.month} onChange={(e) => setBday((b) => ({ ...b, month: e.target.value }))} data-testid="birthday-month-select"
                  className="bg-alp-dark border border-alp-line rounded px-3 py-2 text-sm text-alp-cream focus:border-alp-gold outline-none">
                  <option value="">Month</option>
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
                <button onClick={saveBirthday} disabled={bdaySaving} data-testid="birthday-save-btn"
                  className="bg-alp-gold text-alp-black px-5 py-2 rounded text-xs tracking-[0.15em] uppercase font-semibold hover:bg-alp-goldlight disabled:opacity-50 inline-flex items-center gap-1.5">
                  {bdaySaving && <Loader2 className="animate-spin" size={13} />} Save
                </button>
                {editBday && <button onClick={() => setEditBday(false)} className="text-alp-gray text-xs underline underline-offset-4 hover:text-alp-gold">Cancel</button>}
              </div>
              <p className="text-alp-gray text-[11px] mt-2">Just the day and month — we never ask for the year.</p>
            </div>
          )}
        </div>

        <div className="mb-10 border border-alp-line rounded-lg bg-alp-card/40 p-5" data-testid="account-redeem-giftcard">
          <p className="text-alp-cream/90 text-sm mb-3 flex items-center gap-2"><Ticket size={16} className="text-alp-gold shrink-0" /> Have a gift card? Redeem it here — the full amount becomes store credit.</p>
          <div className="flex flex-wrap gap-2">
            <input value={gcCode} onChange={(e) => setGcCode(e.target.value.toUpperCase())} placeholder="GIFT-XXXXXXXX" data-testid="giftcard-code-input"
              className="flex-1 min-w-[180px] bg-alp-dark border border-alp-line rounded px-3 py-2.5 text-sm text-alp-cream uppercase tracking-[0.1em] focus:border-alp-gold outline-none" />
            <button onClick={redeemGiftCard} disabled={gcBusy} data-testid="giftcard-redeem-btn"
              className="bg-alp-gold text-alp-black px-5 py-2.5 rounded text-xs tracking-[0.15em] uppercase font-semibold hover:bg-alp-goldlight disabled:opacity-50 inline-flex items-center gap-1.5">
              {gcBusy && <Loader2 className="animate-spin" size={13} />} Redeem
            </button>
          </div>
        </div>

        {prefs && (
          <div className="mb-10 border border-alp-line rounded-lg bg-alp-card/40 p-5" data-testid="account-email-prefs">
            <p className="text-alp-cream/90 text-sm mb-4 flex items-center gap-2"><Mail size={16} className="text-alp-gold shrink-0" /> Your email subscriptions</p>
            <div className="space-y-3">
              {[['newStock', 'Back in stock alerts', 'Know the moment a product returns'], ['specials', 'Sales & special offers', 'Promotions, discounts and updates']].map(([key, label, desc]) => (
                <div key={key} className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-alp-cream text-sm">{label}</p>
                    <p className="text-alp-gray text-xs">{desc}</p>
                  </div>
                  <button onClick={() => togglePref(key)} disabled={prefsBusy} data-testid={`pref-toggle-${key}`}
                    className={`shrink-0 text-[11px] tracking-[0.12em] uppercase border rounded-full px-4 py-1.5 transition-colors disabled:opacity-50 ${prefs[key] ? 'border-emerald-400/50 bg-emerald-400/10 text-emerald-300' : 'border-alp-line text-alp-gray hover:border-alp-gold hover:text-alp-gold'}`}>
                    {prefs[key] ? '✓ Subscribed' : 'Subscribe'}
                  </button>
                </div>
              ))}
              <div className="flex items-center justify-between gap-3 pt-3 border-t border-alp-line/50" data-testid="pref-blackfriday">
                <div>
                  <p className="text-alp-cream text-sm">🖤 Black Friday list</p>
                  <p className="text-alp-gray text-xs">Your personal code, emailed at launch</p>
                </div>
                {prefs.blackFriday?.signedUp ? (
                  <span className="shrink-0 inline-flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase border border-alp-gold/50 bg-alp-gold/10 text-alp-gold rounded-full px-4 py-1.5">
                    <Check size={12} /> On the list{prefs.blackFriday.earlyBird && <span className="inline-flex items-center gap-0.5"><Zap size={11} /> Early Bird</span>}
                  </span>
                ) : (
                  <Link to="/black-friday" data-testid="pref-bf-join-link"
                    className="shrink-0 text-[11px] tracking-[0.12em] uppercase border border-alp-line text-alp-gray rounded-full px-4 py-1.5 hover:border-alp-gold hover:text-alp-gold transition-colors">
                    Join the list →
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {affiliate.length > 0 && (
          <div className="mb-10" data-testid="account-affiliate-panel">
            <h2 className="text-alp-cream text-lg font-serif mb-4 flex items-center gap-2"><BadgePercent size={18} className="text-alp-gold" /> Your Affiliate Code</h2>
            {affiliate.map((a) => (
              <div key={a.code} className="border border-alp-gold/30 rounded-lg bg-alp-card/40 p-5 mb-4" data-testid={`affiliate-card-${a.code}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <span className="font-serif text-2xl text-alp-gold tracking-[0.15em]" data-testid="affiliate-code">{a.code}</span>
                  <span className={`text-[10px] tracking-[0.15em] uppercase border rounded-full px-3 py-1 ${a.active ? 'text-emerald-300 border-emerald-400/40' : 'text-alp-gray border-alp-line'}`}>{a.active ? 'Active' : 'Inactive'} &middot; {a.commissionPercent}% commission</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gray mb-1">Orders with your code</p>
                    <p className="text-alp-cream text-xl" data-testid="affiliate-order-count">{a.orderCount}</p>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gray mb-1">Commission earned</p>
                    <p className="text-alp-gold text-xl" data-testid="affiliate-earned">${a.commissionEarned.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gray mb-1">Paid to you</p>
                    <p className="text-emerald-300 text-xl" data-testid="affiliate-paid">${a.commissionPaid.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gray mb-1">Pending</p>
                    <p className="text-amber-300 text-xl" data-testid="affiliate-pending">${a.commissionOutstanding.toFixed(2)}</p>
                  </div>
                </div>
                <p className="text-alp-gray text-xs mt-4">Commission is calculated on paid orders and credited to your account as store credit. Share your code and watch it grow.</p>
              </div>
            ))}
          </div>
        )}

        {creditHistory.length > 0 && (
          <div className="mb-10" data-testid="account-credit-history">
            <button onClick={() => setShowLedger((s) => !s)} data-testid="credit-history-toggle" className="w-full flex items-center justify-between border border-alp-line rounded-lg bg-alp-card/40 px-5 py-4 hover:border-alp-gold/50 transition-colors">
              <span className="text-alp-cream text-lg font-serif flex items-center gap-2"><Wallet size={18} className="text-alp-gold" /> Credit History</span>
              <span className="flex items-center gap-3">
                <span className="text-emerald-300 text-sm" data-testid="credit-balance">${Number(user.credit || 0).toFixed(2)} available</span>
                <ChevronDown size={16} className={`text-alp-gray transition-transform ${showLedger ? 'rotate-180 text-alp-gold' : ''}`} />
              </span>
            </button>
            {showLedger && (
              <div className="border border-t-0 border-alp-line rounded-b-lg bg-alp-card/20 divide-y divide-alp-line/50" data-testid="credit-ledger">
                {creditHistory.map((t, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`credit-tx-${i}`}>
                    <div className="min-w-0">
                      <p className="text-alp-cream/90 text-sm truncate">{t.note || (t.amount >= 0 ? 'Credit added' : 'Credit used')}</p>
                      <p className="text-alp-gray text-[11px]">{t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}{t.orderNumber ? ` \u00b7 ${t.orderNumber}` : ''}</p>
                    </div>
                    <span className={`text-sm whitespace-nowrap ${t.amount >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{t.amount >= 0 ? '+' : '\u2212'}${Math.abs(t.amount).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <h2 className="text-alp-cream text-lg font-serif mb-4 flex items-center gap-2"><Package size={18} className="text-alp-gold" /> Order History</h2>

        {ordersLoading ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-alp-gold" size={22} /></div>
        ) : orders.length === 0 ? (
          <div data-testid="account-no-orders" className="border border-alp-line rounded p-8 text-center bg-alp-card/40">
            <p className="text-alp-gray text-sm">No orders yet under this email.</p>
            <Link to="/shop" className="inline-block mt-4 bg-alp-gold text-alp-black px-6 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors">Shop the range</Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="account-orders">
            {orders.map((o) => (
              <div key={o.orderNumber} data-testid={`account-order-${o.orderNumber}`} className="border border-alp-line rounded p-5 bg-alp-card/40">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-alp-gold text-sm tracking-[0.15em]">{o.orderNumber}</p>
                    <p className="text-alp-gray text-xs mt-0.5">{(o.createdAt || '').slice(0, 10)}</p>
                  </div>
                  <span data-testid={`account-order-status-${o.orderNumber}`} className={`text-[11px] tracking-[0.15em] uppercase border px-3 py-1 rounded ${STATUS_STYLE[o.orderStatus] || 'text-alp-cream/80 border-alp-line'}`}>
                    {o.orderStatus}
                  </span>
                </div>
                <div className="mt-4 border-t border-alp-line/50 pt-3 space-y-1.5">
                  {(o.items || []).map((i, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-alp-cream/80">{i.name} <span className="text-alp-gray">({i.size}) &times;{i.qty}</span></span>
                      <span className="text-alp-cream">${(i.price * i.qty).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 border-t border-alp-line/50 pt-3 flex justify-between items-center">
                  <span className="text-alp-gray text-xs">Payment: <span className="text-alp-cream/90">{o.paymentStatus || 'Unpaid'}</span></span>
                  <span className="text-alp-gold text-sm">Total ${Number(o.total || 0).toFixed(2)} AUD</span>
                </div>
                {o.trackingNumber ? (
                  <a href={o.trackingUrl || '#'} target="_blank" rel="noreferrer" data-testid={`account-track-${o.orderNumber}`} className="mt-3 inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-sky-300 hover:text-sky-200">
                    <Truck size={14} /> Track parcel · {o.trackingNumber}
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- Logged out: login / register / forgot / reset ----------
  const titles = { login: 'Sign In', register: 'Create Account', forgot: 'Reset Password', reset: 'Choose a New Password' };
  const subs = {
    login: 'Sign in to view your past orders and their status.',
    register: 'Create a password to track your orders and status.',
    forgot: "Enter your email and we'll send you a reset link.",
    reset: 'Enter a new password for your account.',
  };
  const cta = { login: 'Sign In', register: 'Create Account', forgot: 'Send Reset Link', reset: 'Update Password' };
  return (
    <div className="max-w-md mx-auto px-5 py-16">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-full border border-alp-gold/40 flex items-center justify-center mx-auto mb-4 text-alp-gold">
          <User size={22} />
        </div>
        <h1 className="font-serif text-3xl text-alp-cream heading-glow">{titles[mode]}</h1>
        <p className="text-alp-gray text-sm mt-2">{subs[mode]}</p>
      </div>

      <form onSubmit={submit} className="border border-alp-line rounded p-6 bg-alp-card/40 space-y-4">
        {mode === 'register' && (
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-1.5">Full Name</label>
            <input value={form.fullName} onChange={(e) => setf('fullName', e.target.value)} required data-testid="account-name-input" className="w-full bg-alp-dark border border-alp-line text-alp-cream px-3 py-2.5 rounded focus:border-alp-gold outline-none" />
          </div>
        )}
        {mode !== 'reset' && (
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-1.5">Email</label>
            <input type="email" value={form.email} onChange={(e) => setf('email', e.target.value)} required data-testid="account-email-input" className="w-full bg-alp-dark border border-alp-line text-alp-cream px-3 py-2.5 rounded focus:border-alp-gold outline-none" />
          </div>
        )}
        {mode !== 'forgot' && (
          <div>
            <label className="block text-[10px] tracking-[0.2em] uppercase text-alp-gold mb-1.5">{mode === 'reset' ? 'New Password' : 'Password'}</label>
            <input type="password" value={form.password} onChange={(e) => setf('password', e.target.value)} required data-testid="account-password-input" className="w-full bg-alp-dark border border-alp-line text-alp-cream px-3 py-2.5 rounded focus:border-alp-gold outline-none" />
            {(mode === 'register' || mode === 'reset') && <p className="text-alp-gray text-[11px] mt-1.5">At least 6 characters.</p>}
          </div>
        )}
        {mode === 'login' && (
          <div className="text-right -mt-1">
            <button type="button" onClick={() => setMode('forgot')} data-testid="account-forgot-link" className="text-alp-gold/90 hover:text-alp-gold text-[11px] underline underline-offset-4">
              Forgot password?
            </button>
          </div>
        )}
        <button type="submit" disabled={submitting} data-testid="account-submit-btn" className="w-full inline-flex items-center justify-center gap-2 bg-alp-gold text-alp-black px-8 py-3 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-alp-goldlight transition-colors disabled:opacity-60">
          {submitting && <Loader2 className="animate-spin" size={15} />}
          {cta[mode]}
        </button>
      </form>

      {(mode === 'forgot' || mode === 'reset') ? (
        <p className="text-center text-alp-gray text-sm mt-5">
          <button onClick={() => { setMode('login'); setSearchParams({}); }} data-testid="account-back-login" className="text-alp-gold hover:text-alp-goldlight underline underline-offset-4">
            Back to sign in
          </button>
        </p>
      ) : (
        <p className="text-center text-alp-gray text-sm mt-5">
          {mode === 'register' ? 'Already have an account?' : 'New here?'}{' '}
          <button
            onClick={() => setMode(mode === 'register' ? 'login' : 'register')}
            data-testid="account-toggle-mode"
            className="text-alp-gold hover:text-alp-goldlight underline underline-offset-4"
          >
            {mode === 'register' ? 'Sign in' : 'Create an account'}
          </button>
        </p>
      )}
    </div>
  );
}
